import { Ticket, User, Message, Operator, Stats, Template, BackendTicketStatus } from '../types';

const API_BASE = (import.meta.env.VITE_API_BASE_URL as string) || 'http://localhost:8120';
const OPERATOR_API_BASE = `${API_BASE}/operator`;
const CSRF_STORAGE_KEY = 'operator_csrf_token';

let csrfToken = typeof window !== 'undefined' ? sessionStorage.getItem(CSRF_STORAGE_KEY) ?? '' : '';

export interface ApiErrorPayload {
  message?: string;
  errors?: Record<string, string[]>;
  error?: {
    message?: string[] | string;
    error?: string;
    statusCode?: number;
  };
  statusCode?: number;
  timestamp?: string;
  path?: string;
}

export class ApiError extends Error {
  status: number;
  payload: ApiErrorPayload | null;

  constructor(status: number, message: string, payload: ApiErrorPayload | null = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.payload = payload;
  }
}

export interface OperatorAccount {
  id: string;
  displayName: string;
  role: Operator['role'];
  login: string | null;
  telegramId: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  lastLoginAt: string | null;
}

export interface OperatorSessionResponse {
  operator: OperatorAccount;
  session: {
    csrfToken: string;
    expiresAt: string;
    refreshExpiresAt: string;
  };
}

interface CurrentOperatorResponse {
  operator: OperatorAccount;
  session?: {
    csrfToken?: string;
  };
}

export interface ApiCustomer {
  id: string;
  telegramId: string;
  username?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  languageCode?: string | null;
  isBlocked?: boolean;
  blockedAt?: string | null;
  lastSeenAt?: string | null;
  operatorNote?: string | null;
  operatorNoteUpdatedAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
  tags?: ApiTagItem[];
}

export interface ApiTagItem {
  id: string;
  code: string;
  label: string;
  color: string | null;
  description: string | null;
}

export interface ApiQuestionnaire {
  categoryCode?: string | null;
  categoryLabel?: string | null;
  description?: string | null;
  orderReference?: string | null;
}

export interface ApiAttachment {
  id: string;
  ticketId: string;
  messageId: string;
  type: 'PHOTO' | 'DOCUMENT' | 'VIDEO' | 'VOICE' | 'VIDEO_NOTE' | 'AUDIO' | 'OTHER';
  telegramFileId: string;
  telegramFileUniqueId: string | null;
  fileName: string | null;
  mimeType: string | null;
  fileSize: number | null;
  caption: string | null;
  createdAt: string;
  downloadUrl?: string | null;
}

export interface ApiMessage {
  id: string;
  ticketId: string;
  senderType: 'CUSTOMER' | 'OPERATOR' | 'SYSTEM';
  direction: 'INBOUND' | 'OUTBOUND' | 'INTERNAL';
  text: string | null;
  caption: string | null;
  operatorId: string | null;
  operatorName: string | null;
  telegramMessageId: number | null;
  externalMessageId: string | null;
  rawUpdateId: number | null;
  createdAt: string;
  updatedAt: string;
  attachments?: ApiAttachment[] | null;
}

export interface ApiTimelineMessage {
  id: string;
  kind: 'MESSAGE' | 'INTERNAL_NOTE' | 'RATING';
  ticketId: string;
  ticketShortId: string;
  ticketStatus: BackendTicketStatus;
  createdAt: string;
  text?: string | null;
  caption?: string | null;
  senderType?: 'CUSTOMER' | 'OPERATOR' | 'SYSTEM';
  direction?: 'INBOUND' | 'OUTBOUND' | 'INTERNAL';
  operatorId?: string | null;
  operatorName?: string | null;
  telegramMessageId?: number | null;
  externalMessageId?: string | null;
  rawUpdateId?: number | null;
  updatedAt?: string;
  attachments?: ApiAttachment[] | null;
  score?: number;
  comment?: string | null;
}

export interface ApiTicketEvent {
  id: string;
  ticketId: string;
  type: string;
  actorType: string | null;
  actorId: string | null;
  actorName: string | null;
  reason: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
}

export interface ApiTicketAssignment {
  id: string;
  ticketId: string;
  operatorId: string;
  operatorName: string | null;
  assignedAt: string;
  unassignedAt: string | null;
  reason: string | null;
}

export interface ApiOrderReference {
  id: string;
  customerId: string;
  ticketId: string;
  value: string;
  kind: string | null;
  source: string;
  createdAt: string;
}

export interface ApiTicketListItem {
  id: string;
  shortId: string;
  customerId: string;
  status: BackendTicketStatus;
  priority: 'HIGH' | 'MEDIUM' | 'LOW';
  categoryCode?: string | null;
  categoryLabel?: string | null;
  description?: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  latestMessageAt?: string | null;
  lastCustomerMessageAt?: string | null;
  lastOperatorMessageAt?: string | null;
  assignedTo?: {
    operatorId: string;
    operatorName: string | null;
    assignedAt: string;
  } | null;
  tags?: ApiTagItem[];
  customer: ApiCustomer;
  questionnaire?: ApiQuestionnaire | null;
}

export interface ApiTicketDetails extends ApiTicketListItem {
  messages: ApiMessage[];
  events: ApiTicketEvent[];
  ratings: Array<{
    id: string;
    score: number;
    comment?: string | null;
    createdAt: string;
  }>;
  assignments: ApiTicketAssignment[];
  internalNotes?: ApiNote[];
  orderReferences: ApiOrderReference[];
}

export interface ApiNote {
  id: string;
  ticketId: string;
  customerId: string;
  operatorId: string | null;
  operatorName: string | null;
  text: string;
  createdAt: string;
}

export interface TelegramOperationalStatus {
  mode: 'polling' | 'webhook';
  telegramConnected: boolean;
  webhook: {
    url?: string;
    hasCustomCertificate: boolean;
    pendingUpdateCount: number;
    maxConnections?: number;
    ipAddress?: string;
    lastErrorDate?: number;
    lastErrorMessage?: string;
    allowedUpdates?: string[];
  } | null;
  commands: Array<{ command: string; description: string }>;
}

type FetchOptions = RequestInit & {
  skipAuthRefresh?: boolean;
};

export function setOperatorCsrfToken(nextToken: string | null): void {
  csrfToken = nextToken?.trim() ?? '';

  if (typeof window === 'undefined') {
    return;
  }

  if (csrfToken) {
    sessionStorage.setItem(CSRF_STORAGE_KEY, csrfToken);
  } else {
    sessionStorage.removeItem(CSRF_STORAGE_KEY);
  }
}

export function getOperatorCsrfToken(): string {
  return csrfToken;
}

function isWriteMethod(method?: string): boolean {
  return ['POST', 'PATCH', 'PUT', 'DELETE'].includes((method ?? 'GET').toUpperCase());
}

async function readError(res: Response): Promise<ApiError> {
  const text = await res.text();

  if (!text) {
    return new ApiError(res.status, `API ${res.status}`);
  }

  try {
    const payload = JSON.parse(text) as ApiErrorPayload;
    const errorMessage = Array.isArray(payload.error?.message)
      ? payload.error?.message.join(', ')
      : payload.error?.message || payload.message || `API ${res.status}`;
    return new ApiError(res.status, errorMessage, payload);
  } catch {
    return new ApiError(res.status, text);
  }
}

async function rawFetch(path: string, options: FetchOptions = {}): Promise<Response> {
  const headers = new Headers(options.headers);
  const isFormDataBody = typeof FormData !== 'undefined' && options.body instanceof FormData;

  if (!headers.has('Content-Type') && options.body && !isFormDataBody) {
    headers.set('Content-Type', 'application/json');
  }

  if (isWriteMethod(options.method) && csrfToken && !headers.has('X-Operator-CSRF')) {
    headers.set('X-Operator-CSRF', csrfToken);
  }

  return fetch(path.startsWith('http') ? path : `${OPERATOR_API_BASE}${path}`, {
    credentials: 'include',
    ...options,
    headers,
  });
}

async function refreshSessionInternal(): Promise<OperatorSessionResponse> {
  const res = await rawFetch('/auth/refresh', {
    method: 'POST',
    skipAuthRefresh: true,
  });

  if (!res.ok) {
    throw await readError(res);
  }

  const payload = (await res.json()) as OperatorSessionResponse;
  setOperatorCsrfToken(payload.session.csrfToken);
  return payload;
}

async function syncCsrfFromCurrentSession(): Promise<boolean> {
  const res = await rawFetch('/auth/me', {
    method: 'GET',
    skipAuthRefresh: true,
  });

  if (!res.ok) {
    return false;
  }

  const payload = (await res.json()) as CurrentOperatorResponse;
  const nextCsrfToken = payload.session?.csrfToken?.trim() ?? '';
  if (!nextCsrfToken) {
    return false;
  }

  setOperatorCsrfToken(nextCsrfToken);
  return true;
}

async function apiFetch<T>(path: string, options: FetchOptions = {}): Promise<T> {
  let res = await rawFetch(path, options);

  if (res.status === 401 && !options.skipAuthRefresh && !path.startsWith('/auth/')) {
    try {
      await refreshSessionInternal();
      res = await rawFetch(path, { ...options, skipAuthRefresh: true });
    } catch {
      setOperatorCsrfToken(null);
    }
  }

  if (!res.ok) {
    const error = await readError(res);

    if (
      res.status === 401 &&
      !options.skipAuthRefresh &&
      !path.startsWith('/auth/') &&
      isWriteMethod(options.method) &&
      /invalid operator csrf token/i.test(error.message)
    ) {
      const synced = await syncCsrfFromCurrentSession();
      if (synced) {
        const retry = await rawFetch(path, { ...options, skipAuthRefresh: true });
        if (!retry.ok) {
          throw await readError(retry);
        }

        if (retry.status === 204) {
          return undefined as T;
        }

        return retry.json() as Promise<T>;
      }
    }

    throw error;
  }

  if (res.status === 204) {
    return undefined as T;
  }

  return res.json() as Promise<T>;
}

export function mapBackendStatus(status: BackendTicketStatus): Ticket['status'] {
  if (status === 'NEW' || status === 'OPEN') {
    return 'open';
  }

  if (status === 'IN_PROGRESS' || status === 'WAITING_USER' || status === 'WAITING_OPERATOR') {
    return 'pending';
  }

  return 'closed';
}

export function mapFrontendStatus(status: Ticket['status']): BackendTicketStatus {
  if (status === 'open') {
    return 'OPEN';
  }

  if (status === 'pending') {
    return 'IN_PROGRESS';
  }

  return 'CLOSED';
}

export function mapOperatorAccount(operator: OperatorAccount): Operator {
  return {
    id: operator.id,
    name: operator.displayName,
    role: operator.role,
    login: operator.login,
    telegramId: operator.telegramId,
    isActive: operator.isActive,
    isOnline: !!operator.lastLoginAt && Date.now() - new Date(operator.lastLoginAt).getTime() < 10 * 60 * 1000,
    createdAt: operator.createdAt,
    updatedAt: operator.updatedAt,
    lastLoginAt: operator.lastLoginAt,
  };
}

export function mapApiCustomer(customer: ApiCustomer, tickets: Ticket[] = []): User {
  const customerTickets = tickets.filter((ticket) => ticket.userId === customer.id);
  const totalMessages = customerTickets.reduce((sum, ticket) => sum + ticket.messages.length, 0);

  return {
    id: customer.id,
    telegramId: customer.telegramId,
    firstName: customer.firstName ?? '',
    lastName: customer.lastName ?? '',
    username: customer.username ? `@${customer.username}` : '',
    isOnline: !!customer.lastSeenAt && Date.now() - new Date(customer.lastSeenAt).getTime() < 5 * 60 * 1000,
    firstSeenAt: customer.createdAt ?? new Date().toISOString(),
    lastSeenAt: customer.lastSeenAt ?? null,
    totalTickets: customerTickets.length,
    totalMessages,
    tags: customer.tags?.map((item) => item.label) ?? [],
    operatorNote: customer.operatorNote ?? '',
    linkedOrders: [],
    languageCode: customer.languageCode ?? null,
    isBlocked: customer.isBlocked ?? false,
    blockedAt: customer.blockedAt ?? null,
    updatedAt: customer.updatedAt ?? customer.createdAt ?? new Date().toISOString(),
  };
}

export function mapApiMessage(
  message: ApiMessage,
  context?: { ticketId?: string; ticketShortId?: string },
): Message {
  const senderType: Message['senderType'] =
    message.senderType === 'CUSTOMER' ? 'user' : message.senderType === 'OPERATOR' ? 'operator' : 'system';

  const firstAttachment = message.attachments?.[0];

  const attachment =
    firstAttachment
      ? {
          type: firstAttachment.type === 'PHOTO' ? ('image' as const) : ('file' as const),
          url: firstAttachment.downloadUrl || '',
          name: firstAttachment.fileName || firstAttachment.type,
          size: firstAttachment.fileSize ? `${Math.round(firstAttachment.fileSize / 1024)} KB` : undefined,
        }
      : undefined;

  return {
    id: message.id,
    text: message.text || message.caption || '',
    senderType,
    senderId: message.operatorId || undefined,
    timestamp: message.createdAt,
    status: senderType === 'operator' ? 'read' : 'delivered',
    isInternalNote: message.direction === 'INTERNAL',
    ticketId: context?.ticketId ?? message.ticketId,
    ticketShortId: context?.ticketShortId,
    attachment,
  };
}

function mapEventMessage(event: ApiTicketEvent): Message {
  return {
    id: `event_${event.id}`,
    text: formatEventText(event),
    senderType: 'system',
    timestamp: event.createdAt,
    status: 'read',
  };
}

function mapPriority(priority: ApiTicketListItem['priority']): Ticket['priority'] {
  if (priority === 'HIGH') {
    return 'high';
  }

  if (priority === 'LOW') {
    return 'low';
  }

  return 'medium';
}

export function mapFrontendPriority(priority: Ticket['priority']): ApiTicketListItem['priority'] {
  if (priority === 'high') {
    return 'HIGH';
  }

  if (priority === 'low') {
    return 'LOW';
  }

  return 'MEDIUM';
}

export function mapApiTicket(ticket: ApiTicketListItem, messages: Message[] = []): Ticket {
  const isClosedLikeStatus = ['CLOSED', 'CANCELED', 'SPAM'].includes(ticket.status);
  const hasUnreadFromCustomer =
    !isClosedLikeStatus &&
    Boolean(ticket.lastCustomerMessageAt) &&
    (!ticket.lastOperatorMessageAt ||
      new Date(ticket.lastCustomerMessageAt as string).getTime() >
        new Date(ticket.lastOperatorMessageAt).getTime());

  return {
    id: ticket.id,
    userId: ticket.customerId,
    shortId: ticket.shortId,
    subject: ticket.questionnaire?.description || ticket.description || ticket.categoryLabel || ticket.shortId,
    status: mapBackendStatus(ticket.status),
    backendStatus: ticket.status,
    priority: mapPriority(ticket.priority),
    assignedTo: ticket.assignedTo?.operatorId ?? null,
    createdAt: ticket.createdAt,
    lastMessageAt: ticket.latestMessageAt || ticket.updatedAt,
    isRead: !hasUnreadFromCustomer,
    unreadCount: hasUnreadFromCustomer ? 1 : 0,
    tags: ticket.tags?.map((tag) => tag.label) ?? [],
    messages,
    linkedOrderId: ticket.questionnaire?.orderReference || undefined,
  };
}

function buildStats(tickets: Ticket[], operators: Operator[]): Stats {
  const openTickets = tickets.filter((ticket) => ticket.status !== 'closed').length;
  const closedTickets = tickets.filter((ticket) => ticket.status === 'closed').length;

  return {
    totalTickets: tickets.length,
    totalTicketsTrend: 0,
    openTickets,
    avgResponseTimeMin: 0,
    closedPeriod: closedTickets,
    chartByDay: [],
    chartResponseTime: [],
    operatorPerformance: operators.map((operator) => ({
      operatorId: operator.id,
      tickets: tickets.filter((ticket) => ticket.assignedTo === operator.id).length,
      closed: tickets.filter((ticket) => ticket.assignedTo === operator.id && ticket.status === 'closed').length,
      avgTimeMin: 0,
    })),
  };
}

function createTemplates(): Template[] {
  return [];
}

export const api = {
  setOperatorCsrfToken,
  getOperatorCsrfToken,

  checkHealth: async (): Promise<boolean> => {
    try {
      const res = await fetch(`${API_BASE}/health`, { signal: AbortSignal.timeout(3000) });
      return res.ok;
    } catch {
      return false;
    }
  },

  getCurrentOperator: async (): Promise<OperatorAccount> => {
    const response = await apiFetch<CurrentOperatorResponse>('/auth/me');
    if (response.session?.csrfToken) {
      setOperatorCsrfToken(response.session.csrfToken);
    }
    return response.operator;
  },

  updateCurrentOperator: async (payload: Partial<{
    displayName: string;
    telegramId: string;
    login: string;
    currentPassword: string;
    newPassword: string;
  }>): Promise<Operator> => {
    const response = await apiFetch<{ operator: OperatorAccount }>('/auth/me', {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
    return mapOperatorAccount(response.operator);
  },

  loginWithTelegram: async (initData: string): Promise<OperatorSessionResponse> => {
    const payload = await apiFetch<OperatorSessionResponse>('/auth/telegram', {
      method: 'POST',
      body: JSON.stringify({ initData }),
    });
    setOperatorCsrfToken(payload.session.csrfToken);
    return payload;
  },

  loginWithPassword: async (login: string, password: string): Promise<OperatorSessionResponse> => {
    const payload = await apiFetch<OperatorSessionResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ login, password }),
    });
    setOperatorCsrfToken(payload.session.csrfToken);
    return payload;
  },

  refreshSession: async (): Promise<OperatorSessionResponse> => refreshSessionInternal(),

  logout: async (): Promise<void> => {
    try {
      await apiFetch<{ ok: true }>('/auth/logout', {
        method: 'POST',
        skipAuthRefresh: true,
      });
    } finally {
      setOperatorCsrfToken(null);
    }
  },

  getOperators: async (): Promise<Operator[]> => {
    const raw = await apiFetch<OperatorAccount[]>('/operators');
    return raw.map(mapOperatorAccount);
  },

  createOperator: async (payload: {
    displayName: string;
    role: Operator['role'];
    telegramId?: string;
    login?: string;
    password?: string;
    isActive?: boolean;
  }): Promise<Operator> => {
    const raw = await apiFetch<OperatorAccount>('/operators', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return mapOperatorAccount(raw);
  },

  updateOperator: async (
    operatorId: string,
    payload: Partial<{
      displayName: string;
      role: Operator['role'];
      telegramId: string | null;
      login: string | null;
      password: string;
      isActive: boolean;
    }>,
  ): Promise<Operator> => {
    const raw = await apiFetch<OperatorAccount>(`/operators/${operatorId}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
    return mapOperatorAccount(raw);
  },

  getTickets: async (params?: {
    page?: number;
    limit?: number;
    status?: BackendTicketStatus;
    customerId?: string;
    telegramId?: string;
    assignedTo?: string;
    priority?: ApiTicketListItem['priority'];
    tag?: string;
    createdFrom?: string;
    createdTo?: string;
  }): Promise<{ tickets: Ticket[]; users: User[] }> => {
    const qs = new URLSearchParams();
    if (params?.page) qs.set('page', String(params.page));
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.status) qs.set('status', params.status);
    if (params?.customerId) qs.set('customerId', params.customerId);
    if (params?.telegramId) qs.set('telegramId', params.telegramId);
    if (params?.assignedTo) qs.set('assignedTo', params.assignedTo);
    if (params?.priority) qs.set('priority', params.priority);
    if (params?.tag) qs.set('tag', params.tag);
    if (params?.createdFrom) qs.set('createdFrom', params.createdFrom);
    if (params?.createdTo) qs.set('createdTo', params.createdTo);

    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    const raw = await apiFetch<ApiTicketListItem[]>(`/tickets${suffix}`);
    const tickets = raw.map((ticket) => mapApiTicket(ticket));
    const users = raw.map((ticket) => mapApiCustomer({ ...ticket.customer, id: ticket.customerId }, tickets));

    return { tickets, users };
  },

  getTicketDetails: async (ticketId: string): Promise<{ ticket: Ticket; customer: User | null }> => {
    const raw = await apiFetch<ApiTicketDetails>(`/tickets/${ticketId}`);
    const messageList = (raw.messages ?? [])
      .map(mapApiMessage)
      .filter((message) => message.senderType !== 'system');
    const noteMessages = (raw.internalNotes ?? []).map((note) => ({
      id: note.id,
      text: note.text,
      senderType: 'operator' as const,
      senderId: note.operatorId ?? undefined,
      timestamp: note.createdAt,
      status: 'read' as const,
      isInternalNote: true,
    }));
    const ratingMessages = (raw.ratings ?? []).map((rating) => {
      const comment = rating.comment?.trim();
      return {
        id: `rating_${rating.id}`,
        text: comment
          ? `Оценка клиента: ${rating.score}/5. Комментарий: ${comment}`
          : `Оценка клиента: ${rating.score}/5`,
        senderType: 'system' as const,
        timestamp: rating.createdAt,
        status: 'read' as const,
        ticketId: raw.id,
        ticketShortId: raw.shortId,
      };
    });
    const allMessages = [...messageList, ...noteMessages, ...ratingMessages].sort(
      (left, right) => new Date(left.timestamp).getTime() - new Date(right.timestamp).getTime(),
    );
    const ticket = mapApiTicket(raw, allMessages);

    const customer = raw.customer?.id
      ? mapApiCustomer(
          {
            ...(raw.customer as ApiCustomer),
            id: String((raw.customer as ApiCustomer).id),
            telegramId: String((raw.customer as ApiCustomer).telegramId ?? ''),
          },
          [ticket],
        )
      : null;

    return { ticket, customer };
  },

  getCustomer: async (customerId: string, tickets: Ticket[] = []): Promise<User> => {
    const raw = await apiFetch<ApiCustomer>(`/customers/${customerId}`);
    return mapApiCustomer(raw, tickets);
  },

  getCustomerTimeline: async (customerId: string): Promise<Message[]> => {
    const raw = await apiFetch<ApiTimelineMessage[]>(`/customers/${customerId}/timeline`);
    return raw
      .map((item) => {
        if (item.kind === 'INTERNAL_NOTE') {
          return {
            id: item.id,
            text: item.text ?? '',
            senderType: 'operator' as const,
            senderId: item.operatorId ?? undefined,
            timestamp: item.createdAt,
            status: 'read' as const,
            isInternalNote: true,
            ticketId: item.ticketId,
            ticketShortId: item.ticketShortId,
          };
        }

        if (item.kind === 'RATING') {
          const score = typeof item.score === 'number' ? item.score : 0;
          const comment = item.comment?.trim();
          return {
            id: `rating_${item.id}`,
            text: comment
              ? `Оценка клиента: ${score}/5. Комментарий: ${comment}`
              : `Оценка клиента: ${score}/5`,
            senderType: 'system' as const,
            timestamp: item.createdAt,
            status: 'read' as const,
            ticketId: item.ticketId,
            ticketShortId: item.ticketShortId,
          };
        }

        const message: ApiMessage = {
          id: item.id,
          ticketId: item.ticketId,
          senderType: item.senderType ?? 'SYSTEM',
          direction: item.direction ?? 'INTERNAL',
          text: item.text ?? null,
          caption: item.caption ?? null,
          operatorId: item.operatorId ?? null,
          operatorName: item.operatorName ?? null,
          telegramMessageId: item.telegramMessageId ?? null,
          externalMessageId: item.externalMessageId ?? null,
          rawUpdateId: item.rawUpdateId ?? null,
          createdAt: item.createdAt,
          updatedAt: item.updatedAt ?? item.createdAt,
          attachments: item.attachments ?? [],
        };

        return mapApiMessage(message, {
          ticketId: item.ticketId,
          ticketShortId: item.ticketShortId,
        });
      })
      .filter((message) => message.senderType !== 'system' || message.id.startsWith('rating_'))
      .sort((left, right) => new Date(left.timestamp).getTime() - new Date(right.timestamp).getTime());
  },

  sendMessage: async (
    ticketId: string,
    payload: { text?: string; mediaType?: string; mediaFileId?: string; file?: File },
  ): Promise<{ attemptId: string }> => {
    if (payload.file) {
      const formData = new FormData();
      if (payload.text) formData.append('text', payload.text);
      if (payload.mediaType) formData.append('mediaType', payload.mediaType);
      formData.append('file', payload.file);

      return apiFetch<{ attemptId: string }>(`/tickets/${ticketId}/messages`, {
        method: 'POST',
        body: formData,
        headers: {},
      });
    }

    return apiFetch<{ attemptId: string }>(`/tickets/${ticketId}/messages`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  createNote: async (ticketId: string, payload: { text: string }): Promise<ApiNote> => {
    return apiFetch<ApiNote>(`/tickets/${ticketId}/notes`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  editMessage: async (ticketId: string, messageId: string, text: string): Promise<{ ok: boolean; id: string; text: string }> =>
    apiFetch(`/tickets/${ticketId}/messages/${messageId}/edit`, {
      method: 'POST',
      body: JSON.stringify({ text }),
    }),

  deleteMessage: async (ticketId: string, messageId: string): Promise<{ ok: boolean; id: string }> =>
    apiFetch(`/tickets/${ticketId}/messages/${messageId}`, {
      method: 'DELETE',
    }),

  updateTicketStatus: async (
    ticketId: string,
    payload: { status: BackendTicketStatus; reason?: string },
  ): Promise<{ id: string; shortId: string; status: BackendTicketStatus; isActive: boolean }> => {
    return apiFetch(`/tickets/${ticketId}/status`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  assignTicket: async (
    ticketId: string,
    payload: { operatorId?: string | null; operatorName?: string; reason?: string },
  ): Promise<ApiTicketAssignment> => {
    return apiFetch<ApiTicketAssignment>(`/tickets/${ticketId}/assign`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  updateTicketPriority: async (
    ticketId: string,
    payload: { priority: ApiTicketListItem['priority'] },
  ): Promise<{ id: string; shortId: string; priority: ApiTicketListItem['priority']; status: BackendTicketStatus }> => {
    return apiFetch(`/tickets/${ticketId}/priority`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  getTicketTagCatalog: async (): Promise<ApiTagItem[]> => apiFetch('/tags/tickets'),
  getCustomerTagCatalog: async (): Promise<ApiTagItem[]> => apiFetch('/tags/customers'),

  addTicketTag: async (ticketId: string, tagId: string) =>
    apiFetch(`/tickets/${ticketId}/tags`, {
      method: 'POST',
      body: JSON.stringify({ tagId }),
    }),

  removeTicketTag: async (ticketId: string, tagId: string) =>
    apiFetch(`/tickets/${ticketId}/tags/${tagId}`, {
      method: 'DELETE',
    }),

  deleteTicket: async (ticketId: string): Promise<{ ok: boolean; ticketId: string; telegram: { attempted: number; deleted: number } }> =>
    apiFetch(`/tickets/${ticketId}`, {
      method: 'DELETE',
    }),

  addCustomerTag: async (customerId: string, tagId: string) =>
    apiFetch(`/customers/${customerId}/tags`, {
      method: 'POST',
      body: JSON.stringify({ tagId }),
    }),

  removeCustomerTag: async (customerId: string, tagId: string) =>
    apiFetch(`/customers/${customerId}/tags/${tagId}`, {
      method: 'DELETE',
    }),

  updateCustomerNote: async (customerId: string, note?: string | null): Promise<User> => {
    const raw = await apiFetch<ApiCustomer>(`/customers/${customerId}/note`, {
      method: 'PUT',
      body: JSON.stringify(note ? { note } : {}),
    });
    return mapApiCustomer(raw);
  },

  blockCustomer: async (customerId: string): Promise<User> => {
    const raw = await apiFetch<ApiCustomer>(`/customers/${customerId}/block`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
    return mapApiCustomer(raw);
  },

  unblockCustomer: async (customerId: string): Promise<User> => {
    const raw = await apiFetch<ApiCustomer>(`/customers/${customerId}/unblock`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
    return mapApiCustomer(raw);
  },

  getTemplates: async (): Promise<Template[]> => {
    const raw = await apiFetch<Array<Template & { createdByOperatorId?: string | null }>>('/templates');
    return raw.map((template) => ({
      id: template.id,
      title: template.title,
      body: template.body,
    }));
  },

  createTemplate: async (payload: Pick<Template, 'title' | 'body'>): Promise<Template> => {
    const raw = await apiFetch<Template>('/templates', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return { id: raw.id, title: raw.title, body: raw.body };
  },

  updateTemplate: async (templateId: string, payload: Partial<Pick<Template, 'title' | 'body'>>): Promise<Template> => {
    const raw = await apiFetch<Template>(`/templates/${templateId}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
    return { id: raw.id, title: raw.title, body: raw.body };
  },

  deleteTemplate: async (templateId: string): Promise<void> => {
    await apiFetch(`/templates/${templateId}`, { method: 'DELETE' });
  },

  getStats: async (period: 'today' | 'week' | 'month' | 'all'): Promise<Stats> => {
    const raw = await apiFetch<{
      totalTickets: number;
      totalTicketsTrend: number;
      openTickets: number;
      avgResponseTimeMin: number;
      avgResponseTimeTrend?: number;
      closedPeriod: number;
      chartByDay: Array<{ date: string; count: number }>;
      chartResponseTime: Array<{ date: string; avgResponseTimeMin: number }>;
      operatorPerformance: Array<{ operatorId: string; operatorName: string | null; replies: number }>;
    }>(`/stats?period=${period}`);

    return {
      totalTickets: raw.totalTickets,
      totalTicketsTrend: raw.totalTicketsTrend,
      openTickets: raw.openTickets,
      avgResponseTimeMin: raw.avgResponseTimeMin,
      closedPeriod: raw.closedPeriod,
      chartByDay: raw.chartByDay.map((item) => ({ date: item.date, tickets: item.count })),
      chartResponseTime: raw.chartResponseTime.map((item) => ({ date: item.date, timeMin: item.avgResponseTimeMin })),
      operatorPerformance: raw.operatorPerformance.map((item) => ({
        operatorId: item.operatorId,
        tickets: item.replies,
        closed: 0,
        avgTimeMin: 0,
      })),
    };
  },

  search: async (query: string, limit = 10): Promise<{
    tickets: Array<{ id: string; shortId: string; status: BackendTicketStatus; priority: ApiTicketListItem['priority']; description?: string | null }>;
    customers: Array<{ id: string; telegramId: string; username?: string | null; firstName?: string | null; lastName?: string | null }>;
    messages: Array<{ id: string; ticketId: string; text: string; createdAt: string; senderType: string }>;
  }> => apiFetch(`/search?q=${encodeURIComponent(query)}&limit=${limit}`),

  getTelegramOperationalStatus: async (): Promise<TelegramOperationalStatus> =>
    apiFetch('/telegram/status'),

  syncTelegramCommands: async (): Promise<{ ok: true; commands: Array<{ command: string; description: string }> }> =>
    apiFetch('/telegram/commands/sync', {
      method: 'POST',
    }),

  buildStats,
  createTemplates,
};

function formatEventText(event: ApiTicketEvent): string {
  const status = event.metadata?.status;
  const known: Record<string, string> = {
    TICKET_CREATED: 'Тикет создан',
    QUESTIONNAIRE_COMPLETED: 'Анкета заполнена',
    MESSAGE_RECEIVED: 'Сообщение получено',
    MESSAGE_SENT: 'Сообщение отправлено',
    TICKET_CLOSED: 'Тикет закрыт',
    TICKET_REOPENED: 'Тикет переоткрыт',
    RATING_RECEIVED: 'Получена оценка',
    ORDER_REFERENCE_ADDED: 'Добавлен номер заказа',
    CUSTOMER_BLOCKED: 'Клиент заблокирован',
    CUSTOMER_UNBLOCKED: 'Клиент разблокирован',
  };

  if (event.type === 'STATUS_CHANGED') {
    return status ? `Статус изменён: ${String(status)}` : 'Статус изменён';
  }

  return known[event.type] ?? event.type;
}
